import { CriteriaSet } from '../../../shared/criteria/domain/criteria';

export type CriteriaKey =
  | 'availability'
  | 'securityAndCompliance'
  | 'performanceAndScalability'
  | 'dataConsistency'
  | 'dataStructure'
  | 'costOptimization'
  | 'dataPersistence';

export const DATABASES_CRITERIA_SET: CriteriaSet<CriteriaKey> = {
  availability: {
    name: 'Availability',
    description:
      "Assess the database's resilience against failures, including backup and disaster recovery mechanisms.",
    prompt:
      "How important is the availability of the application's data and services?",
    weight: 1,
    choices: [
      {
        title: '⭐️⭐️ [Not important]; occasional downtime is acceptable',
        value: 3,
      },
      {
        title:
          '⭐️⭐️⭐️ [Somewhat important]; prefer minimal downtime but can tolerate occasional interruptions',
        value: 6,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️ [Very important]; aim for high availability but can tolerate minimal downtime for maintenance or upgrades',
        value: 8,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; it must be highly available at all times; no tolerance for downtime',
        value: 10,
      },
    ],
  },
  securityAndCompliance: {
    name: 'Security and Compliance',
    description:
      "Consider the database's security features, such as encryption, access control, and compliance with regulatory standards.",
    weight: 0.8,
    prompt:
      "When it comes to your application's data, how much emphasis do you place on security and compliance?",
    choices: [
      {
        title:
          '⭐️⭐️ [Not a priority]; basic security measures are sufficient',
        value: 3,
      },
      {
        title:
          '⭐️⭐️⭐️ [Some importance]; require standard security practices and compliance with relevant regulations',
        value: 6,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️ [Quite important]; prioritize robust security measures and strive for compliance with industry standards',
        value: 8,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [A top priority]; stringent security and compliance measures are crucial to protect sensitive data',
        value: 10,
      },
    ],
  },
  performanceAndScalability: {
    name: 'Performance and Scalability',
    description:
      "Evaluate the database's ability to handle high volumes of data and concurrent requests while maintaining optimal performance.",
    weight: 1,
    prompt:
      'How important is it for your application to handle high loads and scale efficiently?',
    choices: [
      {
        title:
          '⭐️ [Not a concern]; application performance under heavy loads is not critical',
        value: 2,
      },
      {
        title:
          '⭐️⭐️⭐️ [Somewhat important]; aim for reasonable performance and limited scalability as needed',
        value: 5,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️ [Very important]; require good performance and the ability to scale to handle increasing user demand',
        value: 8,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Critical]; performance and scalability are key requirements for optimal user experience and business growth',
        value: 10,
      },
    ],
  },
  dataConsistency: {
    name: 'Data Consistency',
    description:
      'Assess the accurate and valid data storage. Key criteria include ACID compliance, transaction management, concurrency control, data validation and integrity constraints, replication and synchronization mechanisms, and reliable data recovery and backup strategies.',
    weight: 0.75,
    prompt:
      'How significant is maintaining data consistency and integrity within your application?',
    choices: [
      {
        title:
          '⭐️ [Not a priority]; minor inconsistencies or occasional data discrepancies are acceptable',
        value: 2,
      },
      {
        title:
          '⭐️⭐️⭐️ [Some importance]; aim for a reasonable level of data consistency but can tolerate minor inconsistencies in rare cases',
        value: 5,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️ [Quite important]; strive for strong data consistency and integrity across the application',
        value: 8,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Absolutely critical]; data consistency is paramount; zero tolerance for any data inconsistencies',
        value: 10,
      },
    ],
  },
  dataStructure: {
    name: 'Data Structure',
    description:
      "Consider the Data Model such as relational, document-oriented, graph, or key-value. The data model should align with your data's nature and your application's requirements. Also, consider the ability to handle evolving data requirements and accommodate changes to the data structure. Features such as schema evolution, support for dynamic schema, or schemaless design can enhance flexibility.",
    weight: 0.75,
    prompt:
      "Regarding your application's data organization and representation, how much importance do you assign to a well-defined data structure?",
    choices: [
      {
        title:
          '⭐️⭐️ [Not a concern]; flexibility in data structure is preferred, even if it sacrifices some consistency',
        value: 3,
      },
      {
        title:
          '⭐️⭐️⭐️ [Some importance]; require a reasonable level of data structure, but flexibility is still valued',
        value: 5,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️ [Quite important]; prioritize a well-defined and efficient data structure while allowing some degree of flexibility',
        value: 8,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [A top priority]; a well-structured data model is essential for optimal performance and maintainability',
        value: 10,
      },
    ],
  },
  costOptimization: {
    name: 'Cost Optimization',
    description:
      'Involves assessing the total cost of ownership and optimizing expenses. Consider licensing and deployment costs, storage and compute costs, scaling, and elasticity features, maintenance and administration expenses, and cost efficiency optimizations.',
    weight: 1,
    prompt:
      "How much emphasis do you place on optimizing costs associated with your application's database solution?",
    choices: [
      {
        title:
          '⭐️ [Not a priority]; cost considerations are minimal compared to other factors',
        value: 2,
      },
      {
        title:
          '⭐️⭐️ [Some importance]; seek cost-efficient options while balancing performance and functionality',
        value: 4,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️ [Quite important]; prioritize cost optimization while ensuring the database meets performance and scalability requirements',
        value: 7,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [A top priority]; cost optimization is crucial for budget management and minimizing expenses while maintaining desired capabilities',
        value: 10,
      },
    ],
  },

  dataPersistence: {
    name: 'Data Persistence',
    description:
      'To store data in a non-volatile medium, such as a hard drive, so that it can be retrieved even after the database server is shut down. This is important for ensuring that data is not lost during a power outage or other unexpected event.',
    weight: 1,
    prompt: 'How important is data persistence for your system?',
    choices: [
      {
        title:
          '⭐️ [Not crucial]: My data, such as caching or session data, is not mission-critical and can be easily recreated.',
        value: 1,
      },
      {
        title:
          "⭐️⭐️ [Important]: My data is important but not critical. I can afford to lose a few days' worth of data if something happens.",
        value: 3,
      },
      {
        title:
          '⭐️⭐️⭐️ [Critical]: My data is essential and I cannot afford to lose any of it.',
        value: 7,
      },
      {
        title:
          '⭐️⭐️⭐️⭐️⭐️ [Absolutely critical]: My data is life-critical and I need to ensure that it is always available.',
        value: 10,
      },
    ],
  },
};
